import { useMutation, useQueryClient } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { BrandKitAvatar } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { brandKitAvatarsCacheKey } from './useBrandKitAvatars'

interface Data {
  avatar: BrandKitAvatar
}

export interface CreateAvatarVariables {
  name: string
  media_file_id?: number
  catalog_key?: string | null
  persona_note?: string
  age?: string
  ethnicity?: string
  enabled?: boolean
  voice_preset?: string | null
  voice_media_file_id?: number | null
}

type Options = Omit<UseMutationOptions<Data, Error, CreateAvatarVariables>, 'mutationFn'>

export const useCreateBrandKitAvatar = ({ brandKitId }: { brandKitId?: number }, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, CreateAvatarVariables>(
    async (variables) => {
      if (!brandKitId) throw new Error('brandKitId is required')
      const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${brandKitId}/avatars`, {
        method: 'POST',
        body: variables,
      })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to create avatar')
      }
      return res.data
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(brandKitAvatarsCacheKey(workspaceId, brandKitId))
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
